/* eslint-disable no-console */
import { DURATION_TYPE } from '../models/SimpleModels';
import _postalCodeToTimezone from './postalCodeToTimezone';
export const isDateString = (string) => !Number.isNaN(Date.parse(string));
export const isDateObject = (test) => test instanceof Date && !Number.isNaN(test.getTime());
export const isStandardDateFormat = (string) => (/(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.]\d\d\d\d/.test(string));
export const isISOFormat = (string) => (/([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(string));
export const parseDate = (date) => {
    if (isDateObject(date)) {
        return date;
    }
    if (typeof date === 'string' && isDateString(date)) {
        if (isStandardDateFormat(date)) {
            return new Date(date);
        }
        if (isISOFormat(date)) {
            const dateTime = date.split('T');
            if (dateTime.length > 1) {
                return new Date(date);
            }
            const [year, month, day] = dateTime[0].split('-');
            return new Date(`${month}/${day}/${year}`);
        }
    }
    // warn if value is given, but not parsable.
    if (date && date !== '') {
        console.warn(new Error(`Value: [${date}] cannot be parsed into a date. Please pass a string in either "MM/DD/YYYY" or ISO-8601 formats.`));
    }
    return null;
};
export const toMidnight = (dateObj, useUTC = false) => {
    if (useUTC) {
        const midnight = new Date(dateObj).setUTCHours(0, 0, 0, 0);
        return new Date(midnight);
    }
    const midnight = new Date(dateObj).setHours(0, 0, 0, 0);
    return new Date(midnight);
};
export const addDays = (dateObj, numOfDays) => {
    // create a new date object to not mutate the original.
    const date = new Date(dateObj);
    date.setDate(date.getDate() + numOfDays);
    return date;
};
export const addYears = (dateObj, numOfYears) => {
    const current = new Date(dateObj);
    const year = current.getFullYear();
    const month = current.getMonth();
    const day = current.getDate();
    const newDate = new Date(year + numOfYears, month, day);
    // If the month is February and the day is 29
    if (month === 1 && day === 29) {
        // If the new date is not a leap year and the day is not 29 (rolled over to March 1)
        if ((newDate.getFullYear() % 4 !== 0 || (newDate.getFullYear() % 100 === 0 && newDate.getFullYear() % 400 !== 0)) && newDate.getDate() !== 29) {
            // Subtract one day to make it February 28
            newDate.setDate(newDate.getDate() - 1);
        }
    }
    return newDate;
};
export const differenceInDays = (dateObj1, dateObj2) => {
    const minuend = toMidnight(dateObj1).valueOf();
    const subtrahend = toMidnight(dateObj2).valueOf();
    const rawDiff = minuend - subtrahend;
    const daysApart = Math.floor(rawDiff / (1000 * 60 * 60 * 24));
    return daysApart;
};
export const getAge = (dateString) => {
    const now = new Date();
    const yearNow = now.getFullYear();
    const monthNow = now.getMonth();
    const dateNow = now.getDate();
    const dob = parseDate(dateString);
    if (!dob) {
        console.warn(new Error('Could not parse DOB as a date. Please pass a string in either "MM/DD/YYYY" or ISO-8601 formats.'));
        return null;
    }
    const yearDob = dob.getFullYear();
    const monthDob = dob.getMonth();
    const dateDob = dob.getDate();
    const age = {
        year: null,
        month: null,
        date: null,
    };
    age.year = yearNow - yearDob;
    if (monthNow >= monthDob) {
        age.month = monthNow - monthDob;
    }
    else {
        age.year -= 1;
        age.month = 12 + monthNow - monthDob;
    }
    if (dateNow >= dateDob) {
        age.date = dateNow - dateDob;
    }
    else {
        age.month -= 1;
        age.date = 31 + dateNow - dateDob;
        if (age.month < 0) {
            age.month = 11;
            age.year -= 1;
        }
    }
    return age.year;
};
export const formatToStandardDate = (dateObj) => new Date(dateObj).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
});
export const isSameDate = (dateObj1, dateObj2) => {
    if (!isDateObject(dateObj1)) {
        console.error(new Error('First argument in isSameDate is not a date object.'));
        return null;
    }
    if (!isDateObject(dateObj2)) {
        console.error(new Error('Second argument in isSameDate is not a date object.'));
        return null;
    }
    const day1 = toMidnight(dateObj1);
    const day2 = toMidnight(dateObj2);
    return day1.valueOf() === day2.valueOf();
};
export const isToday = (dateAsString) => isSameDate(new Date(), parseDate(dateAsString));
export const isFutureDay = (dateObj) => {
    const day = toMidnight(dateObj);
    const today = new Date(); // no need to zero out time for today.
    return day.valueOf() > today.valueOf();
};
export const getEndDateFromStartDate = (startDate, duration) => {
    const durationTypes = Object.values(DURATION_TYPE);
    if (!startDate) {
        console.error(new Error('Start date is required.'));
        return null;
    }
    if (!duration) {
        console.error(new Error('Duration is required.'));
        return null;
    }
    if (!durationTypes.includes(duration)) {
        console.error(new Error(`Duration must be one of the following: ${durationTypes.toString()}.`));
        return null;
    }
    const parsedStart = parseDate(startDate);
    if (!parsedStart) {
        console.warn(new Error('Could not get end date. Please ensure startDate is a string in "MM/DD/YYYY" or ISO-8601 formats.'));
        return null;
    }
    let endDate;
    if (duration === 'DAY') {
        // if start date is today and duration is one day, make end date the next day.
        endDate = isSameDate(parsedStart, new Date())
            ? addDays(parsedStart, 1)
            : parsedStart;
    }
    else if (duration === 'MONTH') {
        // 31 days of coverage, add 30 to start date (inclusive)
        endDate = addDays(parsedStart, 30);
    }
    else if (duration === 'YEAR') {
        // subtract 1 day from duration since start date is inclusive
        // TODO: add leap year functionality.
        const oneYear = addYears(parsedStart, 1);
        endDate = addDays(oneYear, -1);
    }
    else if (duration === 'WEEK') {
        // subtract 1 day from duration since start date is inclusive
        endDate = addDays(parsedStart, 6);
    }
    return formatToStandardDate(endDate);
};
export const getToday = (_timezone) => {
    // start with a standard UTC date
    const d = new Date();
    if (_timezone) {
        const dateTimeAdjustedForTimezone = d.toLocaleString('en-US', { timeZone: _timezone });
        // split the date from the date time string; we can ignore the time.
        const dateAdjustedForTimezone = dateTimeAdjustedForTimezone.split(',')[0].trim();
        // toLocaleString tries to be helpful by striping 0's from the month and day, so we need to add them back
        const dateAdjustedForTimezoneSplit = dateAdjustedForTimezone.split('/');
        const dateByTimezone = [
            dateAdjustedForTimezoneSplit[0].padStart(2, '0'),
            dateAdjustedForTimezoneSplit[1].padStart(2, '0'),
            dateAdjustedForTimezoneSplit[2], // year should be good
        ].join('/');
        // toLocaleString with the 'en-US' arg will return in our default format
        return dateByTimezone;
    }
    // set time to 00:00:0000 to avoid any timing issues.
    const dToMidnight = toMidnight(d);
    return formatToStandardDate(dToMidnight);
};
export const isBeforeDay = (dateOne, dateTwo) => differenceInDays(dateOne, dateTwo) < 0;
export const isAfterDay = (dateOne, dateTwo) => differenceInDays(dateOne, dateTwo) > 0;
export const Age = {
    isOver18: (dob) => !!(getAge(dob) >= 18),
    isUnder70: (dob) => !!(getAge(dob) < 70),
    isUnder65: (dob) => !!(getAge(dob) < 65),
    isUnder18: (dob) => !!(getAge(dob) < 18),
    isBetween65and70: (dob) => !!(Age.isUnder70(dob) && getAge(dob) >= 65),
};
export const postalCodeToTimezone = _postalCodeToTimezone;
